import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { sparketBlack, sparketGold, sparketLightGray } from "utils/constants.ts";
import CurrencyInput from "react-currency-input-field";

interface PresetAmount {
  amount: number;
  disabled?: boolean;
}

interface AmountSelectorProps {
  presetAmounts: PresetAmount[];
  setSelectedAmount: (amount: number) => void;
  selectedAmount: number;
  placeholder?: string;
  disabled?: boolean;
  maxAmount?: number;
}

const AmountSelector: React.FC<AmountSelectorProps> = ({
  presetAmounts,
  setSelectedAmount,
  selectedAmount,
  placeholder = "$ other amount",
  disabled = false,
  maxAmount,
}) => {

  const [mode, setMode] = useState<"preset" | "custom">(presetAmounts.length > 0 ? "preset" : "custom");
  const [customAmountText, setCustomAmountText] = useState("");

  const setCustomAmountAsSelectedAmount = (customAmountString: string) => {
    const parsedValue = parseFloat(customAmountString);
    if (!isNaN(parsedValue)) {
      if (maxAmount !== undefined && parsedValue > maxAmount) {
        setSelectedAmount(maxAmount);
        setCustomAmountText(maxAmount.toString());
      } else {
        setSelectedAmount(parsedValue);
      }
    } else {
      setSelectedAmount(0);
    }
  };

  const handleAmountChange = (value: string) => {
    const parsedValue = parseFloat(value);
    if (maxAmount !== undefined && !isNaN(parsedValue) && parsedValue > maxAmount) {
      const cappedValue = maxAmount.toString();
      setCustomAmountText(cappedValue);
      setCustomAmountAsSelectedAmount(cappedValue);
    } else {
      setCustomAmountText(value);
      setCustomAmountAsSelectedAmount(value);
    }
  };

  return (
    <div style={{ padding: "8px 4px" }}>
      {/* Single Amount Display */}
      {presetAmounts.length === 1 ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '20px' 
        }}>
          <p style={{
            fontSize: '1.2rem',
            fontWeight: '500',
            color: '#FFFFFF',
            margin: 0,
          }}>
            Amount: ${presetAmounts[0].amount}
          </p>
        </div>
      ) : (
        <>
          {/* Amount Section Header */}
          <div style={{ 
            textAlign: 'center',
            marginBottom: presetAmounts.length > 0 ? '20px' : '16px'
          }}>
            <h5 style={{ 
              fontSize: '1rem', 
              fontWeight: 'bold',
              color: '#FFFFFF',
              margin: 0
            }}>
              Amount
            </h5>
          </div>
          
          {/* Preset Amount Buttons */}
          {presetAmounts.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <Row>
                {presetAmounts.map((preset) => {
                  const isSelected = mode === "preset" && selectedAmount === preset.amount;
                  const isButtonDisabled = disabled || preset.disabled;
                  return (
                    <Col xs={4} key={preset.amount} style={{ marginBottom: '8px' }}>
                      <Button
                        variant="outline-secondary"
                        disabled={isButtonDisabled}
                        style={{
                          width: '100%',
                          color: isButtonDisabled ? '#999' : sparketBlack,
                          fontWeight: isSelected ? 'bolder' : '600',
                          borderRadius: '5px',
                          backgroundColor: isButtonDisabled ? '#ccc' : sparketLightGray,
                          border: 'none',
                          outline: isButtonDisabled ? 'none' : (isSelected ? `4px solid ${sparketGold}` : '1px solid white'),
                          padding: '8px 12px',
                          fontSize: '14px',
                          opacity: isButtonDisabled ? 0.6 : 1,
                          cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => {
                          if (!isButtonDisabled) {
                            setMode("preset");
                            setSelectedAmount(preset.amount);
                          }
                        }}
                      >
                        ${preset.amount}
                      </Button>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
          
          {/* Custom Amount Input */}
          <div style={{ 
            marginTop: presetAmounts.length > 0 ? '20px' : '0px',
            padding: presetAmounts.length > 0 ? '20px 0 0' : '0',
            borderTop: presetAmounts.length > 0 ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
          }}>
            <Row>
              <Col>
                <CurrencyInput 
                  value={customAmountText}
                  prefix={"$"}
                  inputMode={"decimal"}
                  maxLength={5}
                  placeholder={placeholder}
                  allowDecimals={true}
                  disabled={disabled}
                  style={{
                    width: '100%',
                    fontWeight: mode === "custom" ? 'bolder' : '600',
                    outline: mode === "custom" ? `4px solid ${sparketGold}` : '1px solid white',
                    border: 'none',
                    borderRadius: '5px',
                    backgroundColor: sparketLightGray,
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: sparketBlack,
                    height: '40px',
                  }}
                  onFocus={() => {
                    if (!disabled) {
                      setMode("custom");
                      setCustomAmountAsSelectedAmount(customAmountText);
                    }
                  }}
                  onValueChange={(value) => {
                    if (!disabled) {
                      if (value != undefined) {
                        handleAmountChange(value);
                        setMode("custom");
                      } else {
                        handleAmountChange("");
                      }
                    }
                  }} 
                />
              </Col>
            </Row>
          </div>
        </>
      )}
    </div>
  );
};

export default AmountSelector;
